import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import
{
    EmployeeProfileSecurityViewModel,
    EmployeeProfileViewModel,
    ExecutionResult,
    IInfoCardData,
    ISearchCriteria,
    TypeAheadViewModel,
} from "@tylertech/tyler-api-types";
import { AbstractControl, FormGroup } from "@angular/forms";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { EmployeeManagerService, EmployeeSearchService } from "../../services";
import { InfoCardService } from "@tylertech/info-card-module";
@Component({
    selector: "employee-lookup-card",
    templateUrl: "./employeeLookup.tmpl.html",
    styleUrls: [
        "./employeeLookup.component.scss"
    ],
    standalone: false
})
export class EmployeeLookupComponent implements OnInit, OnDestroy
{
    private readonly $employeeService = inject(EmployeeSearchService);
    private readonly $employeeManagerService = inject(EmployeeManagerService);
    private readonly $infoCardService = inject(InfoCardService);

    private readonly destructor$ = new Subject<void>();

    private readonly employeesSubject = new BehaviorSubject<Array<TypeAheadViewModel>>([]);

    readonly formGroup: FormGroup;

    entity: EmployeeProfileViewModel;
    infoCardCriteria: ISearchCriteria<IInfoCardData>;
    isLoaded: boolean;

    // TODO: nothing ever reads this, what is it for?
    private role: EmployeeProfileSecurityViewModel;

    private selectedEmployee: TypeAheadViewModel;
    infoCard: IInfoCardData;

    constructor()
    {
        this.formGroup = new FormGroup({
            employee: LgdFormControlFactory.typeahead<TypeAheadViewModel>(
                undefined,
                false,
                {
                    displayValueFn: x =>
                    {
                        return x
                            ? `${x.ShortDesc} - ${x.LongDesc}`
                            : "";
                    }
                }),
        });
    }

    private get employeeControl(): AbstractControl<TypeAheadViewModel>
    {
        return this.formGroup.get("employee");
    }

    private get employeeId(): string | undefined
    {
        return this.selectedEmployee?.Id;
    }

    get hasEntity(): boolean { return !!this.entity; }

    get employeesObservable(): Observable<Array<TypeAheadViewModel>>
    {
        return this.employeesSubject.asObservable();
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        const self = this;

        this.infoCardCriteria = {
            Settings: { "viewCommand": true }
        };

        this.fetch();

        this.employeeControl.setValue(this.selectedEmployee);
        subscribeToValueChanges(this.employeeControl, (newValue: TypeAheadViewModel) =>
        {
            this.selectedEmployee = newValue;
            this.fetch();
        });
        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    private fetch(): void
    {
        if (this.employeeId)
        {
            this.isLoaded = false;
            Promise.all(
                [
                    this.$employeeManagerService.GetProfileSecurity()
                        .then(
                            (role: EmployeeProfileSecurityViewModel): void =>
                            {
                                this.role = role;
                            }),
                    this.$employeeManagerService.GetEmployeeProfile(this.employeeId)
                        .then(x => this.load(x))
                ])
                .then(() =>
                {
                    this.isLoaded = true;
                });
        }
        else
        {
            this.load();
        }
    }

    private load(emp?: EmployeeProfileViewModel): void
    {
        if (emp && emp.EmployeeId === this.employeeId)
        {
            emp.IsEditable = false;
            this.entity = emp;

            this.$infoCardService.Get({
                Id: emp.EmployeeId,
                CardProviderId: "Employee.InfoCardProvider",
                Criteria: this.infoCardCriteria,
            })
                .then(
                    (item: IInfoCardData) =>
                    {
                        this.infoCard = item;
                    },
                    error =>
                    {
                        console.error(error);
                    });
        }
        else
        {
            this.entity = {} as EmployeeProfileViewModel;
        }
    }

    lookupEmployees($event: string): void
    {
        this.$employeeService.Lookup({ SearchText: $event })
            .then((executionResult: ExecutionResult<Array<TypeAheadViewModel>>) =>
            {
                this.employeesSubject.next(executionResult.Result);
            });
    }
}
